import { formatMoneyShort } from "@/components/Plan/MoneyInput";
import ActualsCell from "./ActualsCell";
import ForecastAccountsCell, {
  type ForecastCustomers,
  type ForecastMonths,
} from "./ForecastAccountsCell";
import ProductNamePopover from "./ProductNamePopover";
import QtyCellInput from "./QtyCellInput";
import { parsePastedRow } from "../utils/parsePaste";
import { type CellStatus } from "../utils/useSaveStatus";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";

interface MonthCell {
  period_date: string;
  quantity: number;
  average_selling_price: number;
  actual_quantity: number | null;
  actual_revenue?: number | null;
  actual_gm: number | null;
}

interface AspCell {
  price: number;
  currency: string;
}

interface Row {
  bottom_up_product_id: string;
  product_external_id: string;
  product_name: string;
  product_sku: string | null;
  plan_node_id: string;
  asp: AspCell | null;
  cogs: number | null;
  months: MonthCell[];
}

interface Props {
  row: Row;
  rowIndex: number;
  displayName: string;
  editable: boolean;
  canViewGrossMargin: boolean;
  planCurrency?: string | null;
  isPastMonth: (iso: string) => boolean;
  onCellChange: (
    productExternalId: string,
    periodDate: string,
    quantity: number,
    fillSelection?: boolean,
  ) => void;
  onPasteCells: (
    productExternalId: string,
    updates: { period_date: string; quantity: number }[],
  ) => void;
  saveStatusFor?: (productExternalId: string, periodDate: string) => CellStatus | undefined;
  isCellSelected: (rowIndex: number, colIndex: number) => boolean;
  onCellMouseDown: (rowIndex: number, colIndex: number, shiftKey: boolean) => void;
  preview: number | null;
  onPreview: (value: number | null) => void;
  isExpanded: boolean;
  onToggleExpand?: () => void;
  monthCount: number;
  forecastMonths?: ForecastMonths;
  forecastCustomers?: ForecastCustomers;
  // Plan-vs-Actual mode: unit-attainment hairline under past cells' plan number.
  showActuals?: boolean;
  // Vertical hairlines between month columns.
  monthDividers?: boolean;
}

function formatQty(n: number): string {
  if (!n) return "—";
  return n.toLocaleString("en-US");
}

export default function ProductRow({ row, rowIndex, displayName, editable, canViewGrossMargin, planCurrency, isPastMonth, onCellChange, onPasteCells, saveStatusFor, isCellSelected, onCellMouseDown, preview, onPreview, isExpanded, onToggleExpand, monthCount, forecastMonths, forecastCustomers, showActuals = false, monthDividers = showActuals }: Props) {
  const currency = row.asp?.currency ?? planCurrency;
  const hasPastActuals = showActuals && row.months.some((m) => isPastMonth(m.period_date));
  // Paste a row of numbers starting at the anchored month, filling rightward
  // across editable (non-past) months only. Overflow is silently ignored.
  const handlePasteRow = (anchorDate: string, text: string) => {
    const values = parsePastedRow(text);
    if (values.length === 0) return;
    const editableDates = row.months
      .filter((m) => !isPastMonth(m.period_date))
      .map((m) => m.period_date);
    const start = editableDates.indexOf(anchorDate);
    if (start === -1) return;
    const updates: { period_date: string; quantity: number }[] = [];
    for (let i = 0; i < values.length && start + i < editableDates.length; i++) {
      updates.push({ period_date: editableDates[start + i], quantity: values[i] });
    }
    if (updates.length > 0) onPasteCells(row.product_external_id, updates);
  };
  return (
    <tr
      style={{ borderColor: "var(--accent)" }}
      className="group border-b"
    >
      <td
        style={{ background: "var(--background)", borderColor: "var(--border)", width: PLAN_PRODUCT_COL_WIDTH, maxWidth: PLAN_PRODUCT_COL_WIDTH }}
        className={`sticky left-0 z-10 px-3 border-r overflow-hidden${hasPastActuals ? " pt-2 pb-1.5 align-bottom" : " py-2"}`}
      >
        {hasPastActuals ? (
          // Actuals mode: name beside the row labels, not above them, so the
          // product cell is the same height as the month cells and the
          // bottom-anchored rows don't leave dead space above the Plan line.
          // Labels mirror the past cells' row heights (leading-4 + bar
          // spacers) to sit on the same horizontal lines; py-1.5 matches the
          // month cells' effective 6px vertical padding.
          <div className="flex items-end gap-2">
            <div className="flex-1 min-w-0 self-center">
              <ProductNamePopover
                externalId={row.product_external_id}
                displayName={displayName}
                fullName={row.product_name}
                asp={row.asp}
                cogs={row.cogs}
              />
            </div>
            <div
              style={{ color: "var(--muted-foreground)" }}
              className="text-right text-[9px] uppercase tracking-wide"
            >
              <div>
                <div className="leading-4">Units</div>
                <div className="h-0.5" />
              </div>
              <div className="mt-2">
                <div className="leading-4">Rev</div>
                <div className="h-0.5" />
              </div>
            </div>
          </div>
        ) : (
          <ProductNamePopover
            externalId={row.product_external_id}
            displayName={displayName}
            fullName={row.product_name}
            asp={row.asp}
            cogs={row.cogs}
          />
        )}
      </td>
      {row.months.map((cell, idx) => {
        const past = isPastMonth(cell.period_date);
        const qty = cell.quantity;
        // Revenue uses the Products & Pricing target ASP, the same price the
        // month totals row and the node headers use.
        const revenue = (row.asp?.price ?? 0) * qty;
        const isEvenMonthCount = monthCount % 2 === 0;
        const chevronCellIdx = isEvenMonthCount ? monthCount / 2 : Math.floor(monthCount / 2);
        const showChevronInThisCell = idx === chevronCellIdx;
        const selected = editable && !past && isCellSelected(rowIndex, idx);
        return (
          <td
            key={cell.period_date}
            style={{
              ...(monthDividers ? { borderColor: "var(--accent)" } : undefined),
            }}
            className={`px-1 py-0.5 relative${past && showActuals ? " align-bottom" : ""}${monthDividers ? " border-r" : ""}`}
          >
            {past && showActuals ? (
              <ActualsCell
                cell={cell}
                targetAsp={row.asp?.price ?? null}
                cogs={row.cogs}
                currency={currency}
                canViewGrossMargin={canViewGrossMargin}
              />
            ) : past ? (
              <span
                style={{ color: "var(--foreground)" }}
                className="block w-full text-right text-xs tabular-nums px-1.5 py-1"
                title={`Actual sales: ${formatQty(cell.actual_quantity ?? 0)}`}
              >
                {formatQty(cell.quantity)}
              </span>
            ) : editable ? (
              <QtyCellInput
                initial={cell.quantity}
                row={rowIndex}
                col={idx}
                selected={selected}
                status={saveStatusFor?.(row.product_external_id, cell.period_date)}
                thousands
                preview={preview}
                onPreview={onPreview}
                onCommit={(v, fillSelection) =>
                  onCellChange(row.product_external_id, cell.period_date, v ?? 0, fillSelection)
                }
                onPasteRow={(text) => handlePasteRow(cell.period_date, text)}
                onSelectMouseDown={(shiftKey) => onCellMouseDown(rowIndex, idx, shiftKey)}
              />
            ) : (
              <span
                style={{ color: "var(--foreground)" }}
                className="block w-full text-right text-xs tabular-nums px-1.5 py-1"
              >
                {showActuals && !qty ? "" : formatQty(qty)}
              </span>
            )}
            {revenue > 0 && !(past && showActuals) && (
              <span
                style={{ color: "var(--muted-foreground)" }}
                className="block w-full text-right text-[10px] tabular-nums px-1.5 mt-0.5"
                title="Revenue (Target ASP × units)"
              >
                {formatMoneyShort(revenue, currency)}
              </span>
            )}
            {forecastMonths && forecastCustomers && (
              <ForecastAccountsCell
                monthIso={cell.period_date}
                months={forecastMonths}
                customers={forecastCustomers}
                productExternalId={row.product_external_id}
              />
            )}
            {showChevronInThisCell && onToggleExpand && (
              <button
                type="button"
                onClick={onToggleExpand}
                aria-label={isExpanded ? "Collapse context" : "Expand context"}
                style={{
                  background: "var(--background)",
                  borderColor: "var(--border)",
                  color: "var(--muted-foreground)",
                }}
                className={`absolute ${isEvenMonthCount ? "left-0" : "left-1/2"} -translate-x-1/2 -bottom-2 z-10 h-4 w-6 border rounded-md flex items-center justify-center leading-none`}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                  <path
                    d={isExpanded ? "M2 6 L5 3 L8 6" : "M2 4 L5 7 L8 4"}
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </td>
        );
      })}
    </tr>
  );
}
